import { useMutation, UseMutationOptions } from 'react-query';
import { API_SERVICE_URL } from 'configs/env.conf';

const S3_VIDEO_UPLOAD_BASE_URL = `${API_SERVICE_URL}/video-upload/s3`;

/**
 * Initialize S3 Video Upload
 * POST /video-upload/s3/init
 */
export type InitS3VideoUploadRequest = {
  unitId: number;
  mimeType?: string;
};

export type InitS3VideoUploadResponse = {
  id: number;
  unit_id: number;
  s3_key: string;
  s3_bucket: string;
  upload_url: string;
  video_url: string;
  mime_type: string;
};

export const initS3VideoUpload = async (
  request: InitS3VideoUploadRequest,
): Promise<InitS3VideoUploadResponse> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${S3_VIDEO_UPLOAD_BASE_URL}/init`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Init S3 upload failed');
  }

  const data = await response.json();
  // Handle both direct response and nested data structure
  const result = data.data || data;

  if (!result.upload_url) {
    throw new Error('upload_url is missing from S3 init response');
  }

  return result;
};

/**
 * Complete S3 Video Upload
 * POST /video-upload/s3/complete
 */
export type CompleteS3VideoUploadRequest = {
  id: number;
  fileSize?: number;
  duration?: number;
};

export type CompleteS3VideoUploadResponse = {
  id: number;
  unit_id: number;
  video_url: string;
  s3_key: string;
  s3_bucket: string;
  file_size?: number;
  duration?: number;
  unitVideo: string[];
};

export const completeS3VideoUpload = async (
  request: CompleteS3VideoUploadRequest,
): Promise<CompleteS3VideoUploadResponse> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${S3_VIDEO_UPLOAD_BASE_URL}/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Complete S3 upload failed');
  }

  const data = await response.json();
  // Handle both direct response and nested data structure
  // Response can be: {video_url: "..."} or {data: {video_url: "..."}, _status: 200}
  const result = data.data || data;
  return result;
};

/**
 * Delete S3 Video
 * POST /video-upload/s3/delete
 */
export type DeleteS3VideoRequest = {
  unitId: number;
  videoUrl: string;
};

export type DeleteS3VideoResponse = {
  ok: boolean;
  message: string;
  unitVideo: string[];
};

export const deleteS3Video = async (
  request: DeleteS3VideoRequest,
): Promise<DeleteS3VideoResponse> => {
  const token = localStorage.getItem('token');

  const response = await fetch(`${S3_VIDEO_UPLOAD_BASE_URL}/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Delete S3 video failed');
  }

  return response.json();
};

/**
 * Upload video file to S3 using presigned URL
 * PUT {upload_url}
 */
export const uploadVideoToS3 = async (
  uploadUrl: string,
  videoFile: File,
  onProgress?: (progress: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', uploadUrl);

    // Set Content-Type header
    xhr.setRequestHeader('Content-Type', videoFile.type || 'video/mp4');

    // Track upload progress
    xhr.upload.onprogress = e => {
      if (e.lengthComputable && onProgress) {
        const progress = Math.round((e.loaded / e.total) * 100);
        onProgress(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed with status: ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during S3 upload'));
    };

    xhr.send(videoFile);
  });
};

/**
 * React Query hooks
 */
export const useInitS3VideoUpload = (
  opts?: UseMutationOptions<
    InitS3VideoUploadResponse,
    Error,
    InitS3VideoUploadRequest
  >,
) => {
  return useMutation<
    InitS3VideoUploadResponse,
    Error,
    InitS3VideoUploadRequest
  >(initS3VideoUpload, opts);
};

export const useCompleteS3VideoUpload = (
  opts?: UseMutationOptions<
    CompleteS3VideoUploadResponse,
    Error,
    CompleteS3VideoUploadRequest
  >,
) => {
  return useMutation<
    CompleteS3VideoUploadResponse,
    Error,
    CompleteS3VideoUploadRequest
  >(completeS3VideoUpload, opts);
};

export const useDeleteS3Video = (
  opts?: UseMutationOptions<DeleteS3VideoResponse, Error, DeleteS3VideoRequest>,
) => {
  return useMutation<DeleteS3VideoResponse, Error, DeleteS3VideoRequest>(
    deleteS3Video,
    opts,
  );
};
